import type { Shift } from "@src/appV2/Shifts/Shift/types";
import { useMemo } from "react";

import { filterOpenShiftsCount } from "../../ShiftDiscovery/Filters/filterOpenShiftsCount";
import { useShiftDiscoveryUserFiltersContext } from "../../ShiftDiscovery/Filters/useUserFiltersContext";
import {
  groupShiftsByDateAndTimeSlot,
  type ShiftsByDateAndTimeSlot,
} from "../../utils/groupShiftsByDateAndTimeSlot";
import type { OpenShift } from "../Open/types";
import type { OpenShiftDatesResponse } from "../Open/usePaginatedOpenShiftDates";
import { mergeUrgentShiftsWithCountData } from "./mergeUrgentShiftsWithCountData";
import type { OpenShiftDatesResponseWithUrgent } from "./types";

interface UseCalendarDataProcessingProps {
  workerShiftsData?: {
    pages: Array<{ agentShifts: Array<Pick<Shift, "facility" | "start" | "name">> }>;
  };
  workerShiftsIsSuccess: boolean;
  paginatedOpenShiftsCountData?: {
    pages: Array<{ data: OpenShiftDatesResponse }>;
  };
  urgentShifts: OpenShift[];
  timezone: string;
}

interface CalendarDataProcessingResult {
  workerShiftsByDate: ShiftsByDateAndTimeSlot;
  unfilteredOpenShiftsCountDataWithUrgency: OpenShiftDatesResponseWithUrgent;
  filteredOpenShiftsCountData: OpenShiftDatesResponseWithUrgent;
}

export function useCalendarDataProcessing(
  props: UseCalendarDataProcessingProps
): CalendarDataProcessingResult {
  const {
    workerShiftsData,
    workerShiftsIsSuccess,
    paginatedOpenShiftsCountData,
    urgentShifts,
    timezone,
  } = props;

  const { shiftTimeSlots } = useShiftDiscoveryUserFiltersContext();

  const workerShiftsByDate = useMemo(() => {
    if (!workerShiftsIsSuccess || !workerShiftsData) {
      return {};
    }

    return groupShiftsByDateAndTimeSlot(
      workerShiftsData.pages.flatMap((page) => page.agentShifts) ?? [],
      timezone
    );
  }, [workerShiftsIsSuccess, workerShiftsData, timezone]);

  const unfilteredOpenShiftsCountData = useMemo(() => {
    return (
      paginatedOpenShiftsCountData?.pages.reduce<OpenShiftDatesResponse>((result, page) => {
        return {
          ...result,
          ...page.data,
        };
      }, {}) ?? {}
    );
  }, [paginatedOpenShiftsCountData]);

  const unfilteredOpenShiftsCountDataWithUrgency = useMemo(() => {
    return mergeUrgentShiftsWithCountData(unfilteredOpenShiftsCountData, urgentShifts);
  }, [unfilteredOpenShiftsCountData, urgentShifts]);

  const filteredOpenShiftsCountData = useMemo(() => {
    return filterOpenShiftsCount(unfilteredOpenShiftsCountDataWithUrgency, {
      shiftTimeSlots,
    });
  }, [unfilteredOpenShiftsCountDataWithUrgency, shiftTimeSlots]);

  return {
    workerShiftsByDate,
    unfilteredOpenShiftsCountDataWithUrgency,
    filteredOpenShiftsCountData,
  };
}
